import React, { useEffect, useMemo, useRef, useState } from "react";
import { GoogleMap } from '@react-google-maps/api';

import { getHeatMap } from "../context/firebaseConfig";
import { useFirebaseAuth } from "../context/AuthContext";

import { ConfigProvider, DatePicker, Layout } from 'antd';
import locale from 'antd/locale/es_ES';

import { useGoogleMaps } from '../context/GoogleMapsContext';
import dayjs from 'dayjs';
import Loading from '../components/Loading';
import { DEFAULT_MAP_CENTER, DEFAULT_MAP_ZOOM, normalizeLatLng, normalizeMapCenter, normalizeMapZoom } from '../constants/maps';

const { Content } = Layout;

const containerStyle = {
    width: '100%',
    height: '94vh'
};

const actionsMenuWrapper = {
    position: 'absolute',
    'zIndex': '10',
    top: '80px',
    left: '45%',
}

const FORMAT = 'YYYY-MM-DD HH';

const createColorPalette = () => {
    const canvas = document.createElement('canvas');
    canvas.width = 256;
    canvas.height = 1;

    const ctx = canvas.getContext('2d');
    const gradient = ctx.createLinearGradient(0, 0, 256, 0);
    gradient.addColorStop(0.1, 'rgba(0, 0, 255, 1)');
    gradient.addColorStop(0.35, 'rgba(0, 255, 255, 1)');
    gradient.addColorStop(0.55, 'rgba(0, 255, 0, 1)');
    gradient.addColorStop(0.75, 'rgba(255, 255, 0, 1)');
    gradient.addColorStop(1, 'rgba(255, 0, 0, 1)');

    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, 256, 1);

    return ctx.getImageData(0, 0, 256, 1).data;
};

const drawHeatmap = (canvas, points, projection, bounds, radius) => {
    const ctx = canvas.getContext('2d');
    const ne = projection.fromLatLngToDivPixel(bounds.getNorthEast());
    const sw = projection.fromLatLngToDivPixel(bounds.getSouthWest());
    const width = Math.max(0, ne.x - sw.x);
    const height = Math.max(0, sw.y - ne.y);

    canvas.style.left = `${sw.x}px`;
    canvas.style.top = `${ne.y}px`;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;

    const pixelRatio = window.devicePixelRatio || 1;
    canvas.width = width * pixelRatio;
    canvas.height = height * pixelRatio;
    ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    ctx.clearRect(0, 0, width, height);

    const maxWeight = Math.max(...points.map(point => point.weight || 1), 1);

    points.forEach(point => {
        const latLng = new window.google.maps.LatLng(point.lat, point.lng);
        const pixel = projection.fromLatLngToDivPixel(latLng);
        const x = pixel.x - sw.x;
        const y = pixel.y - ne.y;
        const alpha = Math.min((point.weight || 1) / maxWeight, 1);
        const gradient = ctx.createRadialGradient(x, y, 0, x, y, radius);

        gradient.addColorStop(0, `rgba(0, 0, 0, ${alpha})`);
        gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');
        ctx.fillStyle = gradient;
        ctx.fillRect(x - radius, y - radius, radius * 2, radius * 2);
    });

    const image = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const pixels = image.data;
    const palette = createColorPalette();

    for (let i = 3; i < pixels.length; i += 4) {
        const alpha = pixels[i];
        if (!alpha) continue;

        const offset = alpha * 4;
        pixels[i - 3] = palette[offset];
        pixels[i - 2] = palette[offset + 1];
        pixels[i - 1] = palette[offset + 2];
        pixels[i] = Math.min(alpha * 1.35, 255);
    }

    ctx.putImageData(image, 0, 0);
};

const HeatmapCanvasOverlay = ({ map, points, radius = 25 }) => {
    const overlayRef = useRef(null);
    const canvasRef = useRef(null);

    useEffect(() => {
        if (!map || !window.google) return;

        const overlay = new window.google.maps.OverlayView();

        overlay.onAdd = () => {
            const canvas = document.createElement('canvas');
            canvas.style.position = 'absolute';
            canvas.style.pointerEvents = 'none';
            canvasRef.current = canvas;
            overlay.getPanes().overlayLayer.appendChild(canvas);
        };

        overlay.draw = () => {
            const canvas = canvasRef.current;
            const projection = overlay.getProjection();
            const bounds = map.getBounds();

            if (!canvas || !projection || !bounds || points.length === 0) return;

            drawHeatmap(canvas, points, projection, bounds, radius);
        };

        overlay.onRemove = () => {
            if (canvasRef.current?.parentNode) {
                canvasRef.current.parentNode.removeChild(canvasRef.current);
            }
            canvasRef.current = null;
        };

        overlay.setMap(map);
        overlayRef.current = overlay;

        return () => {
            overlayRef.current?.setMap(null);
            overlayRef.current = null;
        };
    }, [map, radius, points]);

    return null;
};
  
const HeatMap = () => {
    const [user] = useState(useFirebaseAuth());
    const [markers, setMarks] = useState([]);
    //Establecemos aqui el mapa de España completo por si no hubiese coordenadas y q no de error setCenter
    const [center, setCenter] = useState(DEFAULT_MAP_CENTER); //{lat: 34.5, lng: -3.5}
    const [zoom, setZoom] = useState(DEFAULT_MAP_ZOOM); //5.5
    const [selectedDate, setSelectedDate] = useState();
    const [isLoading, setLoading] = useState(true);
    const [map, setMap] = useState(null);

    const { isLoaded } = useGoogleMaps();

    const heatmapPoints = useMemo(() => {
        return markers
            .map(point => {
                const position = normalizeLatLng(point, null);
                if (!position) return null;

                return {
                    ...position,
                    weight: Number(point.weight) || 1
                };
            })
            .filter(Boolean);
    }, [markers]);
    
    useEffect(() => {
        // desde el contexto establecemos las coordenadas del mapa a mostrar
        setCenter(normalizeMapCenter(user?.configs?.mapCenter));
        setZoom(normalizeMapZoom(user?.configs?.mapZoom));
    }, [user])

    useEffect(() => {
        const d = dayjs();      
        setSelectedDate(d);
        getData(d.format(FORMAT));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    
    const getData = async (dateString) => {
        setLoading(true);
        const ini = new Date(`${dateString}:00:00`).getTime();
        const end = new Date(`${dateString}:59:59`).getTime();
        let data = {};
        data.tk = user?.accessToken;
        data.service = user?.service;
        data.ini = ini;
        data.end = end;
        const ret = await getHeatMap(data);
        setMarks(ret);
        setLoading(false);
    }

    const onChange = async (value, dateString) => {
        // Procesamos la fecha dada y hacemos el setMarks
        setSelectedDate(value);
        await getData(dateString);
    };

    return (
        <React.Fragment>
            <Layout>
                { isLoading ? 
                        <Loading />
                    :
                        <Content>
                            <div style={actionsMenuWrapper}>
                                <ConfigProvider locale={locale}>
                                    <DatePicker
                                        showTime
                                        // defaultValue={dayjs().format(FORMAT)}
                                        onChange={onChange}
                                        value={selectedDate}
                                        format={FORMAT}
                                        size='large'
                                    />
                                </ConfigProvider>
                            </div>
                            { isLoaded &&
                                <GoogleMap
                                    mapContainerStyle={containerStyle}
                                    center={center}
                                    zoom={zoom}
                                    onLoad={setMap}
                                    onUnmount={() => setMap(null)}
                                >
                                    {heatmapPoints.length > 0 && <HeatmapCanvasOverlay map={map} points={heatmapPoints} />}
                                </GoogleMap>
                            }
                        </Content>
                }
            </Layout>
        </React.Fragment>
    )
}

export default HeatMap;
